import {
  FormControl,
  Grid,
  GridProps,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
import { numberFormatAsString } from "commons/ultilities";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import { observer } from "mobx-react-lite";
import { IPackingNoteProperties } from "mobx-stores/_models/packing-note/packing-note";
import { IPackingNoteItemProperties } from "mobx-stores/_models/packing-note/packing-note-item";
import { useStores } from "mobx-stores/_root-store";
import { ReactNode, useCallback, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { PrintPreviewDialog } from "share-components/templates/print/print-preview-dialog";

export const PackingNotePrintPreview = observer(() => {
  const {
    packingNoteManagement: {
      packingNoteDetail,
      packingPrintingManagement: {
        open,
        reset,
        selectedPackingNoteItem,
        variant,
        setProp,
      },
    },
  } = useStores();
  const contentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef,
  });
  const onPrintClicked = useCallback(() => {
    handlePrint();
  }, [handlePrint]);
  const onCloseHandler = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <PrintPreviewDialog
      open={open}
      fullWidth
      maxWidth={variant === "6" ? "lg" : "md"}
      onClose={onCloseHandler}
      onPrintClicked={onPrintClicked}
      headerAction={
        <FormControl fullWidth>
          <InputLabel>Loại phiếu</InputLabel>
          <Select
            value={variant}
            label="Loại phiếu"
            onChange={(e) => setProp("variant", e.target.value as "10" | "6")}
            sx={{ minWidth: "150px" }}
            size="small"
          >
            <MenuItem value={"10"}>10 Phiếu</MenuItem>
            <MenuItem value={"6"}>6 Phiếu</MenuItem>
          </Select>
        </FormControl>
      }
    >
      <style>
        {`@media print {
              @page {
                size: A4 ${variant === "6" ? "landscape" : "portrait"};
              }
              body {
                font-family: "Times New Roman", Times, serif;
              }
          }`}
      </style>
      <Grid
        ref={contentRef}
        container
        sx={{
          width: variant === "6" ? "29.7cm" : "21cm",
          paddingTop: variant === "6" ? "0.3cm" : "0.3cm",
        }}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {selectedPackingNoteItem &&
          packingNoteDetail &&
          Array.from(Array(variant === "10" ? 10 : 6).keys()).map((x) => {
            return variant === "10" ? (
              <Grid
                key={x}
                item
                xs={6}
                alignItems={"center"}
                justifyContent={"center"}
                sx={{ display: "flex", width: "10.5cm", height: "5.75cm" }}
              >
                <PackingItemPrint
                  item={selectedPackingNoteItem}
                  packingNoteDetail={packingNoteDetail}
                ></PackingItemPrint>
              </Grid>
            ) : (
              <Grid
                key={x}
                item
                xs={6}
                alignItems={"center"}
                justifyContent={"center"}
                sx={{
                  display: "flex",
                  width: "14.5cm",
                  height: "6.8cm",
                }}
              >
                <PackingItemPrint
                  item={selectedPackingNoteItem}
                  packingNoteDetail={packingNoteDetail}
                ></PackingItemPrint>
              </Grid>
            );
          })}
      </Grid>
    </PrintPreviewDialog>
  );
});
type PackingItemPrintProps = {
  packingNoteDetail: IPackingNoteProperties;
  item: IPackingNoteItemProperties;
};
const PackingItemPrint = observer(
  ({ item, packingNoteDetail }: PackingItemPrintProps) => {
    const {
      packingNoteManagement: {
        packingPrintingManagement: { variant },
      },
    } = useStores();

    const fontWeight = "800";
    const containerSize = useMemo(() => {
      if (variant === "6") {
        return {
          width: "13.6cm",
          height: "6.5cm",
        };
      } else {
        return {
          width: "9cm",
          height: "5cm",
        };
      }
    }, [variant]);

    const leftGridSize = useMemo(() => {
      return variant === "10" ? [3.5, 8.5] : [3, 9];
    }, [variant]);

    const rightGridSize = useMemo(() => {
      return variant === "10" ? [5.5, 6.5] : [4.5, 7.5];
    }, [variant]);

    return (
      <Grid
        container
        sx={{
          border: `1px solid`,
          padding: "2px 3px",
          lineHeight: "0.5",
          ...containerSize,
        }}
      >
        <GridItem xs={6}>
          <Stack>
            <TypoOfItem lineHeight={1.3}>BAO BÌ GIẤY Á CHÂU</TypoOfItem>
            <TypoOfItem lineHeight={1.3}>
              1524 TL8, X.Bình Mỹ, TP.HCM
            </TypoOfItem>
            <TypoOfItem lineHeight={1.3}>www.baobiachau.vn</TypoOfItem>
          </Stack>
        </GridItem>
        <GridItem xs={6}>
          <Stack direction={"column"} justifyContent={"center"} height={"100%"}>
            <TypoOfItem
              fontWeight={fontWeight}
              fontSize={variant === "10" ? "18px" : "24px"}
            >
              {`Phiếu đóng gói`.toUpperCase()}
            </TypoOfItem>
          </Stack>
        </GridItem>
        <GridItem xs={12}>
          <TypoOfItem
            fontSize={variant === "10" ? "12px" : "16px"}
            fontWeight={fontWeight}
            textOverflow={"clip"}
            textAlign={"center"}
          >
            {`Khách Hàng: ${item.product?.customer?.name || ""}`}
          </TypoOfItem>
        </GridItem>
        <GridItem xs={12}>
          <ItemInfo
            title="Tên hàng"
            value={item.product?.name || ""}
            valueTypoProps={{
              sx: {
                marginLeft: variant === "10" ? "3px" : "8px",
              },
            }}
          ></ItemInfo>
        </GridItem>
        <GridItem xs={6}>
          <ItemInfo
            title="Mã hàng"
            value={item.product?.code || ""}
            gridSize={leftGridSize}
          ></ItemInfo>
        </GridItem>
        <GridItem xs={6}>
          <ItemInfo
            title={
              <TypoOfItem
                component={"span"}
                sx={{ marginLeft: variant === "10" ? "78px" : "96px" }}
              >
                STT
              </TypoOfItem>
            }
            value={""}
          ></ItemInfo>
        </GridItem>
        <GridItem xs={6}>
          <ItemInfo
            title="Đơn ĐH"
            value={packingNoteDetail.purchaseOrder?.poNumber || ""}
            gridSize={leftGridSize}
          ></ItemInfo>
        </GridItem>
        <GridItem xs={6}>
          <ItemInfo
            title="Ngày đặt hàng"
            value={dateTimeDisplay(
              packingNoteDetail.purchaseOrder?.poDate || "",
            )}
            gridSize={rightGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="Ngày ĐG"
            value={dateTimeDisplay(packingNoteDetail.date || "")}
            gridSize={leftGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="Người đóng gói"
            value={packingNoteDetail.kcsPackage || ""}
            gridSize={rightGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="Số lượng"
            value={`${numberFormatAsString(item.amount?.toString() || "")} pcs`}
            gridSize={leftGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="T/L tịnh (kg)"
            value={`${numberFormatAsString(
              item.netWeight?.toString() || "",
            )} kg`}
            gridSize={rightGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="Số lô SX"
            value={
              (packingNoteDetail.workOrderPO?.woNumber ??
                packingNoteDetail.workOrder?.woNumber) ||
              ""
            }
            gridSize={leftGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="T/L có bì (kg)"
            value={`${numberFormatAsString(
              item.grossWeight?.toString() || "",
            )} kg`}
            gridSize={rightGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="Ngày SX"
            value={dateTimeDisplay(
              (packingNoteDetail.workOrderPO?.date ??
                packingNoteDetail.workOrder?.date) ||
                "",
            )}
            gridSize={leftGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="KCS kiểm"
            value={packingNoteDetail.kcsTest || ""}
            gridSize={rightGridSize}
          ></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo title="Số hộp (toa)/bản in" value={""}></ItemInfo>
        </GridItem>

        <GridItem xs={6}>
          <ItemInfo
            title="Hạn tái kiểm"
            value={
              <TypoOfItem component={"span"}>
                {dateTimeDisplay(item.expiredDate) || ""}
              </TypoOfItem>
            }
          ></ItemInfo>
        </GridItem>
      </Grid>
    );
  },
);
type ItemInfoProps = {
  title: ReactNode;
  value: ReactNode;
  gridSize?: number[];
  valueTypoProps?: TypographyProps;
};
const ItemInfo = ({
  title,
  value,
  gridSize,
  valueTypoProps,
}: ItemInfoProps) => {
  return (
    <Grid container alignItems={"center"} spacing={0.5}>
      <Grid item xs={gridSize?.[0]}>
        <TypoOfItem component={"span"} lineHeight={1}>
          {title}
          {`: `}
        </TypoOfItem>
      </Grid>
      <Grid item xs={gridSize?.[1]}>
        <TypoOfItem
          {...valueTypoProps}
          component={"span"}
          sx={{ ...valueTypoProps?.sx, fontWeight: "800" }}
        >
          {value}
        </TypoOfItem>
      </Grid>
    </Grid>
  );
};

const TypoOfItem = (props: TypographyProps) => {
  const {
    packingNoteManagement: {
      packingPrintingManagement: { variant },
    },
  } = useStores();
  const theme = useTheme();
  const fontSize = useMemo(() => {
    return variant === "6" ? "14px" : "10.5px";
  }, [variant]);
  return (
    <Typography
      component={"div"}
      fontSize={fontSize}
      {...props}
      noWrap
      textOverflow={"ellipsis"}
      width={"100%"}
      fontFamily={`"Times New Roman", Times, ${theme.typography.fontFamily} !important`}
    >
      {props.children}
    </Typography>
  );
};

const GridItem = (props: GridProps) => {
  return (
    <Grid
      {...props}
      item
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {props.children}
    </Grid>
  );
};
